import React, { useState } from 'react';
import Cropper from 'react-easy-crop';
import { authAPI } from '../services/api';
import './ImageCropModal.css';

interface ImageCropModalProps {
  image: string;
  onCropComplete: (croppedImage: Blob) => void;
  onCancel: () => void;
  onValidationError?: (error: string) => void;
}

export const ImageCropModal: React.FC<ImageCropModalProps> = ({
  image,
  onCropComplete,
  onCancel,
  onValidationError,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const handleCropAreaChange = (croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleSaveCrop = async () => {
    if (!croppedAreaPixels) {
      return;
    }

    try {
      const canvas = await createImage(image, croppedAreaPixels);
      canvas.toBlob(async (blob) => {
        if (blob) {
          try {
            const validationResponse = await authAPI.validateFace(blob);
            const validationResult = validationResponse.data;
            
            if (!validationResult.has_face) {
              if (onValidationError) {
                onValidationError(validationResult.message);
              }
              return;
            }
            
            onCropComplete(blob);
          } catch (validationError) {
            if (onValidationError) {
              onValidationError('Erro ao validar face. Tente novamente.');
            }
          }
        }
      }, 'image/jpeg');
    } catch (error) {
      console.error('Erro ao fazer crop:', error);
    }
  };

  return (
    <div className="crop-modal-overlay">
      <div className="crop-modal">
        <div className="crop-modal-header">
          <h2>Editar Imagem</h2>
          <button className="crop-modal-close" onClick={onCancel}>
            ✕
          </button>
        </div>

        <div className="crop-modal-container">
          <Cropper
            image={image}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onCropAreaChange={handleCropAreaChange}
            onZoomChange={setZoom}
          />
        </div>

        <div className="crop-modal-controls">
          <div className="crop-zoom-control">
            <label>Zoom:</label>
            <input
              type="range"
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="zoom-slider"
            />
            <span className="zoom-value">{zoom.toFixed(1)}x</span>
          </div>
        </div>

        <div className="crop-modal-footer">
          <button className="btn-cancel" onClick={onCancel}>
            Cancelar
          </button>
          <button className="btn-save" onClick={handleSaveCrop}>
            Salvar Crop
          </button>
        </div>
      </div>
    </div>
  );
};

async function createImage(
  imageSrc: string,
  pixelCrop: any
): Promise<HTMLCanvasElement> {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  image.src = imageSrc;

  return new Promise((resolve, reject) => {
    image.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Não foi possível obter o contexto do canvas'));
        return;
      }

      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;

      ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
      );

      resolve(canvas);
    };

    image.onerror = () => {
      reject(new Error('Erro ao carregar a imagem'));
    };
  });
}

export default ImageCropModal;
